import {
  AvailableContentStates,
  BlogpostReferenceOrContentId,
  Content,
  ContentType,
  Excerpt,
  PageReferenceOrContentId,
  ResolvedBlogpostReference,
  ResolvedBlogpostReferenceOrContentId,
  ResolvedPageReference,
  ResolvedPageReferenceOrContentId,
} from '@databases/types/confluence';
import { AnyAction } from 'redux';
import { ThunkAction } from 'redux-thunk';

import {
  getContentById,
  getCoverPicture,
  resolveContentReference,
} from '../../confluence/content';
import { getAvailableContentStates } from '../../confluence/contentState';
import { getExcerptMacroContent } from '../../confluence/excerpt';
import { serializeError } from '../../utils';
import { selectBaseUrl } from '../context/selectors';
import { CommonRootState, loadAsync } from '../redux';

import {
  selectAvailableContentStates,
  selectAvailableContentStatesStatus,
  selectContentByIdOptional,
  selectContentByIdStatus,
  selectContentRefOptional,
  selectContentRefStatus,
  selectCoverPicture,
  selectCoverPictureStatus,
  selectExcerpt,
  selectExcerptStatus,
} from './selectors';
import contentSlice from './slice';

export const CONTENT_EXPANSIONS = [
  'space',
  'history', // For getting the posting day for blogposts
  'history.lastUpdated',
  'metadata.labels',
  'metadata.properties.content_state_published',
  'operations',
];

type Dispatcher<Result> = ThunkAction<Result, CommonRootState, void, AnyAction>;

/**
 * Load the specified content object into the store. If the object is already loaded, no request is made.
 */
export function loadContentById<Type extends ContentType>(
  contentId: string,
  {
    forceReload = false,
  }: {
    forceReload?: boolean;
  } = {}
): Dispatcher<Promise<Content<Type> | undefined>> {
  return async (dispatch, getState) => {
    await loadAsync(
      () => selectContentByIdStatus(getState(), { contentId }),
      () => {
        dispatch(contentSlice.actions.loadContentByIdPending({ contentId }));
      },
      async () => {
        try {
          return await getContentById(contentId, CONTENT_EXPANSIONS);
        } catch (err: any) {
          if (err.message.includes('does not have view permissions on entity page')) {
            return undefined;
          }
        }
      },
      (obj) => {
        if (obj) {
          dispatch(addContentToList(obj));
        }
        dispatch(contentSlice.actions.loadContentByIdFinished({ contentId }));
      },
      (error) => {
        dispatch(
          contentSlice.actions.loadContentByIdError({
            contentId,
            error: serializeError(error),
          })
        );
      },
      { forceReload }
    );

    return selectContentByIdOptional(getState(), { contentId });
  };
}

/**
 * Resolves the given content reference and loads it into the store. If the content (reference) is already in the store, no request is made.
 */
export const loadContentRef =
  (
    contentRef: PageReferenceOrContentId | BlogpostReferenceOrContentId,
    {
      forceReload = false,
    }: {
      forceReload?: boolean;
    } = {}
  ): Dispatcher<
    Promise<
      ResolvedPageReferenceOrContentId | ResolvedBlogpostReferenceOrContentId | undefined
    >
  > =>
  async (dispatch, getState) => {
    await loadAsync(
      () => selectContentRefStatus(getState(), { contentRef }),
      () => {
        dispatch(contentSlice.actions.loadContentRefPending(contentRef));
      },
      async () =>
        (!forceReload && selectContentRefOptional(getState(), { contentRef })) ||
        (await resolveContentReference(contentRef)),
      (result) => {
        if (result) {
          dispatch(addContentToList(result));
        }

        dispatch(contentSlice.actions.loadContentRefFinished(contentRef));
      },
      (error) => {
        dispatch(
          contentSlice.actions.loadContentRefError({
            ...contentRef,
            error: serializeError(error),
          })
        );
      },
      { forceReload }
    );

    return selectContentRefOptional(getState(), { contentRef });
  };

/**
 * Resolves the cover picture of the given page into the store. If the picture is already in the store, no request is made.
 */
export const loadCoverPicture =
  (
    pageId: string,
    {
      forceReload = false,
    }: {
      forceReload?: boolean;
    } = {}
  ): Dispatcher<Promise<string | undefined>> =>
  async (dispatch, getState) => {
    await loadAsync(
      () => selectCoverPictureStatus(getState(), { contentId: pageId }),
      () => {
        dispatch(contentSlice.actions.loadCoverPicturePending({ contentId: pageId }));
      },
      async () => await getCoverPicture(pageId, selectBaseUrl(getState())),
      (coverPicture) => {
        dispatch(
          contentSlice.actions.loadCoverPictureFinished({
            contentId: pageId,
            coverPicture,
          })
        );
      },
      (error) => {
        dispatch(
          contentSlice.actions.loadCoverPictureError({
            contentId: pageId,
            error: serializeError(error),
          })
        );
      },
      { forceReload }
    );

    return selectCoverPicture(getState(), { contentId: pageId });
  };

/**
 * Add a new content object to the list of content objects. If an object with the same content ID exists already, it is replaced.
 */
export const addContentToList = (
  content:
    | Content
    | ResolvedPageReference
    | ResolvedBlogpostReference
    | Array<Content | ResolvedPageReference | ResolvedBlogpostReference>
): AnyAction =>
  contentSlice.actions.addContentToList({
    content: Array.isArray(content) ? content : [content],
  });

/**
 * Delete a content object from the list of content objects.
 */
export const deleteContentFromList = (
  content: { id: string } | string | Array<{ id: string } | string>
): AnyAction =>
  contentSlice.actions.deleteContentFromList({
    contentIds: (Array.isArray(content) ? content : [content]).map((obj) =>
      typeof obj === 'string' ? obj : obj.id
    ),
  });

export const loadAvailableContentStates =
  (
    contentId: string,
    {
      forceReload = false,
    }: {
      forceReload?: boolean;
    } = {}
  ): Dispatcher<Promise<AvailableContentStates>> =>
  async (dispatch, getState) => {
    await loadAsync(
      () => selectAvailableContentStatesStatus(getState(), { contentId }),
      () => {
        dispatch(contentSlice.actions.loadAvailableContentStatesPending({ contentId }));
      },
      async () => await getAvailableContentStates(contentId),
      (states) => {
        dispatch(
          contentSlice.actions.loadAvailableContentStatesFinished({ contentId, states })
        );
      },
      (error) => {
        dispatch(
          contentSlice.actions.loadAvailableContentStatesError({
            contentId,
            error: serializeError(error),
          })
        );
      },
      { forceReload }
    );

    return selectAvailableContentStates(getState(), { contentId })!;
  };

/**
 * Resolves the excerpt of the given page into the store. If the excerpt in the store, no request is made.
 */
export const loadExcerpt =
  (
    pageRef: ResolvedPageReference | ResolvedBlogpostReference,
    {
      forceReload = false,
    }: {
      forceReload?: boolean;
    } = {}
  ): Dispatcher<Promise<Excerpt | undefined>> =>
  async (dispatch, getState) => {
    await loadAsync(
      () => selectExcerptStatus(getState(), { contentId: pageRef.contentId }),
      () => {
        dispatch(
          contentSlice.actions.loadExcerptPending({ contentId: pageRef.contentId })
        );
      },
      async () => await getExcerptMacroContent(pageRef),
      (excerpt) => {
        dispatch(
          contentSlice.actions.loadExcerptFinished({
            contentId: pageRef.contentId,
            excerpt,
          })
        );
      },
      (error) => {
        dispatch(
          contentSlice.actions.loadExcerptError({
            contentId: pageRef.contentId,
            error: serializeError(error),
          })
        );
      },
      { forceReload }
    );

    return selectExcerpt(getState(), { contentId: pageRef.contentId });
  };

/**
 * Clears the cache of loaded available content states for all pages.
 */
export const resetAvailableContentStates = (): AnyAction =>
  contentSlice.actions.resetAvailableContentState();
